import React, {useRef} from 'react';
import classNames from 'classnames';
import {observer} from 'mobx-react';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import styles from './Select.module.scss';

interface SelectProps {
    value: string | number;
    onChange: (value: string | number) => void;
    onBlur?: () => void;
    values: {value: string | number; label?: string | number; disabled?: boolean}[];
    disabled?: boolean;
    border?: boolean;
    placeholder?: string;
    noLimit?: boolean;
    isSelected?: boolean;
    isFocused?: boolean;
    className?: string;
    label?: string;
    autofocusValue?: string | number;
    error?: string;
}

const Select: React.FC<SelectProps> = ({
    value,
    onChange,
    onBlur,
    values,
    disabled,
    placeholder,
    className,
    label,
    autofocusValue,
    error,
}) => {
    const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

    // Scroll the item matching autofocusValue to top
    const handleMenuEntering = () => {
        setTimeout(() => {
            if (autofocusValue !== undefined && autofocusValue !== null && !value) {
                const index = values.findIndex(({value: v}) => v === autofocusValue);
                if (index !== -1 && itemRefs.current[index]) {
                    itemRefs.current[index]?.scrollIntoView({block: 'start'});
                }
            }
        }, 0);
    };

    return (
        <TextField
            select
            fullWidth
            label={label}
            disabled={disabled}
            value={values?.find(({value: v}) => `${v}`.toLowerCase() === `${value}`.toLowerCase())?.value || ''}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            variant="outlined"
            className={classNames(styles.root, className, {[styles.inputLabel]: !label})}
            placeholder={placeholder}
            error={!!error}
            helperText={error}
            SelectProps={{
                onOpen: handleMenuEntering,
                IconComponent: () => (
                    <svg width="11" height="6" viewBox="0 0 11 6" className={styles.icon}>
                        <path opacity="0.6" d="M5.5 6L0.73686 5.64421e-08L10.2631 8.89256e-07L5.5 6Z" fill="#81828C" />
                    </svg>
                ),
                MenuProps: {
                    classes: {
                        paper: styles.paper,
                    },
                    sx: {
                        maxHeight: '312px',
                        margin: '16px 0',
                        width: '100%',
                    },
                },
            }}
            InputLabelProps={{
                sx: {
                    fontFamily: 'FredokaLight',
                    borderRadius: 0,
                    fontSize: '1.8rem',
                    lineHeight: '140%',
                    color: '#174943',
                    whiteSpace: 'normal',
                    position: 'absolute',
                    top: '50%',
                    transform: 'translate(16px, -50%)',
                    '&.Mui-focused, &.MuiFormLabel-filled': {
                        top: '0',
                        transform: 'translate(16px, -13px) scale(0.75)',
                        color: '#174943',
                        whiteSpace: 'nowrap',
                    },
                },
            }}
            inputProps={{
                'data-testid': 'select',
            }}
            InputProps={{
                classes: {
                    notchedOutline: classNames(styles.notchedOutline, {[styles.error]: !!error}),
                },
            }}
            FormHelperTextProps={{
                classes: {
                    error: styles.errorMessage,
                },
            }}
        >
            {values?.map(({value, label}, index) => (
                <MenuItem
                    value={value}
                    key={label}
                    ref={(el) => (itemRefs.current[index] = el)}
                    sx={{
                        whiteSpace: 'normal',
                        paddingLeft: '20px',
                        fontFamily: 'FredokaLight',
                        textAlign: 'left',
                        fontSize: 18,
                        color: '#174943',
                        justifyContent: 'flex-start',
                        ':hover': {
                            backgroundColor: '#EDFCF2',
                            cursor: 'pointer',
                        },
                        '&.Mui-selected': {
                            backgroundColor: '#EDFCF2',
                            ':hover': {
                                backgroundColor: '#EDFCF2',
                            },
                        },
                    }}
                >
                    {label}
                </MenuItem>
            ))}
        </TextField>
    );
};

export default observer(Select);
